import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import {
  Users, Clock, FileText, DollarSign, Activity, Monitor, Briefcase,
  Bell, LayoutDashboard, TrendingUp, ArrowRight, Calendar, CheckCircle,
  XCircle, AlertCircle, Wallet, Building2
} from 'lucide-react';
import './Dashboard.css';

const currentMonth = () => new Date().toISOString().slice(0, 7);

const iconMap = {
  users: Users,
  clock: Clock,
  file: FileText,
  dollar: DollarSign,
  activity: Activity,
  monitor: Monitor,
  briefcase: Briefcase,
  bell: Bell,
  dashboard: LayoutDashboard,
  trending: TrendingUp,
  arrow: ArrowRight,
  calendar: Calendar,
  check: CheckCircle,
  x: XCircle,
  alert: AlertCircle,
  wallet: Wallet,
  building: Building2,
};

const deriveLinkFromType = (type, entityType, entityId) => {
  if (!entityType) return '/notifications';
  const map = {
    absence: '/all-requests',
    salary_advance: '/salary-mgmt',
    attendance: '/attendance-mgmt',
    task: '/my-tasks',
    project: '/projects',
    request: '/all-requests',
    attestation: '/all-requests',
  };
  return map[entityType] || '/notifications';
};

const StatCard = ({ icon, label, value, color, path, onClick }) => {
  const navigate = useNavigate();
  const Icon = iconMap[icon] || Activity;
  const handleClick = onClick || (path ? () => navigate(path) : undefined);

  return (
    <div className={`stat-card clickable ${color}`} onClick={handleClick} role="button" tabIndex={0}>
      <div className={`stat-icon ${color}`}>
        <Icon size={24} />
      </div>
      <div className="stat-content">
        <h3>{value}</h3>
        <p>{label}</p>
      </div>
      {path && <ArrowRight size={16} className="stat-arrow" />}
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'MANAGER';

  useEffect(() => {
    const fetchDashboardData = async () => {
      const month = currentMonth();
      const nextStats = {};
      const nextActivities = [];

      if (isAdmin) {
        // Admin: team stats
        try {
          const users = await api.get('/users');
          nextStats.totalUsers = Array.isArray(users) ? users.length : 0;
        } catch (e) { nextStats.totalUsers = 0; }

        try {
          const depts = await api.get('/departments');
          nextStats.totalDepartments = Array.isArray(depts) ? depts.length : 0;
        } catch (e) { nextStats.totalDepartments = 0; }

        try {
          const attendance = await api.get('/attendance');
          nextStats.todayAttendance = Array.isArray(attendance) ? attendance.length : 0;
        } catch (e) { nextStats.todayAttendance = 0; }

        try {
          const remote = await api.get('/attendance/remote/active-sessions');
          const remoteArr = Array.isArray(remote) ? remote : (remote?.sessions || []);
          nextStats.activeRemoteSessions = remoteArr.length;
        } catch (e) { nextStats.activeRemoteSessions = 0; }

        try {
          const absences = await api.get('/absences');
          const arr = Array.isArray(absences) ? absences : [];
          nextStats.pendingLeaves = arr.filter(a => a.status === 'pending' || a.status === 'PENDING').length;
        } catch (e) { nextStats.pendingLeaves = 0; }

        try {
          const advances = await api.get('/salary/advances/pending');
          nextStats.pendingAdvances = Array.isArray(advances) ? advances.length : 0;
        } catch (e) { nextStats.pendingAdvances = 0; }

        try {
          const notif = await api.get('/notifications/count');
          nextStats.unreadNotifications = notif?.count || 0;
        } catch (e) { nextStats.unreadNotifications = 0; }
      } else {
        // Employee: personal stats
        try {
          const attendance = await api.get('/attendance/me');
          const arr = Array.isArray(attendance) ? attendance : [];
          nextStats.myAttendance = arr.filter(a => a.date && a.date.startsWith(month)).length;
        } catch (e) { nextStats.myAttendance = 0; }

        try {
          const remote = await api.get('/attendance/remote/status');
          nextStats.remoteActive = remote?.status?.clockedIn && !remote?.status?.clockedOut;
        } catch (e) { nextStats.remoteActive = false; }

        try {
          const absences = await api.get('/absences/me');
          const arr = Array.isArray(absences) ? absences : [];
          nextStats.pendingLeaves = arr.filter(a => a.status === 'pending' || a.status === 'PENDING').length;
        } catch (e) { nextStats.pendingLeaves = 0; }

        try {
          const advances = await api.get('/salary/advances/me');
          const arr = Array.isArray(advances) ? advances : [];
          nextStats.pendingAdvances = arr.filter(a => a.status === 'pending' || a.status === 'PENDING').length;
        } catch (e) { nextStats.pendingAdvances = 0; }

        try {
          const payroll = await api.get(`/salary/me/monthly/${month}`);
          nextStats.netSalary = payroll?.totals?.netSalary ?? 0;
          nextStats.currency = payroll?.currency || 'TND';
        } catch (e) { nextStats.netSalary = 0; nextStats.currency = 'TND'; }

        try {
          const tasks = await api.get('/tasks/me');
          nextStats.myTasks = Array.isArray(tasks) ? tasks.length : 0;
        } catch (e) { nextStats.myTasks = 0; }

        try {
          const notif = await api.get('/notifications/count');
          nextStats.unreadNotifications = notif?.count || 0;
        } catch (e) { nextStats.unreadNotifications = 0; }
      }

      // Recent activity (notifications) for all roles
      try {
        const notifs = await api.get('/notifications');
        const arr = Array.isArray(notifs) ? notifs : [];
        nextActivities.push(...arr.slice(0, 10).map(n => ({
          id: n.id,
          title: n.title || n.message || 'Notification',
          message: n.message || '',
          type: n.type || n.notificationType || 'info',
          createdAt: n.createdAt,
          read: n.read || false,
          link: n.link || deriveLinkFromType(n.type, n.entityType, n.entityId),
        })));
      } catch (e) { /* skip */ }

      setStats(nextStats);
      setActivities(nextActivities);
      setLoading(false);
    };

    fetchDashboardData();
  }, [isAdmin]);

  if (loading) {
    return <div className="dashboard-loading">Loading dashboard...</div>;
  }

  return (
    <div className="dashboard">
      <div className="page-header">
        <h1><LayoutDashboard size={28} style={{ verticalAlign: 'middle', marginRight: 10 }} /> Dashboard</h1>
        <p>Welcome back, <strong>{user?.firstName || user?.email}</strong></p>
      </div>

      {isAdmin ? (
        <>
          <h2 className="section-title">Team Overview</h2>
          <div className="stats-grid">
            <StatCard icon="users" label="Total Users" value={stats.totalUsers || 0} color="blue" path="/users" />
            <StatCard icon="building" label="Departments" value={stats.totalDepartments || 0} color="purple" path="/departments" />
            <StatCard icon="clock" label="Today's Attendance" value={stats.todayAttendance || 0} color="green" path="/attendance-mgmt" />
            <StatCard icon="monitor" label="Active Remote Sessions" value={stats.activeRemoteSessions || 0} color="cyan" path="/attendance-mgmt" />
            <StatCard icon="file" label="Pending Leaves" value={stats.pendingLeaves || 0} color="amber" path="/all-requests" />
            <StatCard icon="dollar" label="Pending Advances" value={stats.pendingAdvances || 0} color="orange" path="/salary-mgmt" />
            <StatCard icon="bell" label="Unread Notifications" value={stats.unreadNotifications || 0} color="red" path="/notifications" />
          </div>
        </>
      ) : (
        <>
          <h2 className="section-title">My Overview</h2>
          <div className="stats-grid">
            <StatCard icon="clock" label="Attendance This Month" value={stats.myAttendance || 0} color="green" path="/my-attendance" />
            <StatCard
              icon="monitor"
              label="Remote Work"
              value={stats.remoteActive ? 'Active' : 'Inactive'}
              color={stats.remoteActive ? 'cyan' : 'gray'}
              path="/remote-work"
            />
            <StatCard icon="file" label="Pending Leaves" value={stats.pendingLeaves || 0} color="amber" path="/my-absences" />
            <StatCard icon="dollar" label="Pending Advances" value={stats.pendingAdvances || 0} color="orange" path="/my-salary" />
            <StatCard icon="bell" label="Unread Notifications" value={stats.unreadNotifications || 0} color="red" path="/notifications" />
            <StatCard icon="briefcase" label="My Tasks" value={stats.myTasks || 0} color="indigo" path="/my-tasks" />
            <StatCard
              icon="wallet"
              label="Net Salary"
              value={`${Number(stats.netSalary || 0).toFixed(2)} ${stats.currency || 'TND'}`}
              color="emerald"
              path="/my-salary"
            />
          </div>
        </>
      )}

      <div className="dashboard-sections">
        <div className="section activity-section">
          <h2><Activity size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} /> Recent Activity</h2>
          <div className="activity-list">
            {activities.length === 0 ? (
              <div className="activity-empty">No recent activity</div>
            ) : (
              activities.map(act => {
                const ActivityIcon = act.type?.toLowerCase().includes('error') || act.type?.toLowerCase().includes('reject')
                  ? XCircle
                  : act.type?.toLowerCase().includes('success') || act.type?.toLowerCase().includes('approve')
                    ? CheckCircle
                    : act.type?.toLowerCase().includes('warning')
                      ? AlertCircle
                      : Bell;
                return (
                  <div
                    key={act.id}
                    className={`activity-item ${act.read ? '' : 'unread'}`}
                    onClick={() => act.link && navigate(act.link)}
                    role="button"
                    tabIndex={0}
                  >
                    <ActivityIcon size={16} className={`activity-icon ${act.type}`} />
                    <div className="activity-body">
                      <div className="activity-title">{act.title}</div>
                      <div className="activity-meta">
                        {act.message && <span className="activity-message">{act.message}</span>}
                        <span className="activity-time">
                          {act.createdAt ? new Date(act.createdAt).toLocaleString() : ''}
                        </span>
                      </div>
                    </div>
                    {!act.read && <span className="activity-dot" />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
